/**
 * Clock is where a product-meaningful moment or delay comes from.
 *
 * Two methods and no more. `now` is the current instant in epoch milliseconds; `after` schedules
 * a callback and hands back the cancel. Everything a surface does with time is one of those, and
 * a seam that also offered intervals, dates or formatting would be four decisions where the
 * product only makes one.
 *
 * **It is for PRODUCT time, not for rendering time.** A banner that stays up for four seconds, a
 * rename that commits after a pause, a hint that re-reads the clock each minute: each of those
 * is a duration somebody chose, and a host embedding the studio in a driving app or a kiosk may
 * choose differently. A `requestAnimationFrame` is not (it is the frame rate, which belongs to
 * the display), so animation stays on the platform and is named on an allowlist in the boundary
 * test rather than routed through here.
 *
 * `after` returns the CANCEL rather than a handle, because every caller does the same thing with
 * a handle and half of them get it wrong: a handle has to be stored, narrowed against
 * null, and cleared through the matching global, and an effect that returns the handle instead
 * of a cleanup silently never cancels anything.
 *
 * **`now` and `after` are one time base.** A delay handed to `after` is spent in the time `now`
 * reads: a caller that waits until an instant computes `instant - now()` and hands it to `after`.
 * An implementation that pins, scales or replaces `now` moves `after` with it, as
 * {@link manualClock} does. With `now` a host's own and `after` the platform's, a wait computed
 * from a pinned `now` never shrinks, and one computed from a `now` that reads no instant is
 * `NaN`, which a platform timer runs at once.
 */
export interface Clock {
  /** Schedules `run` after `delayMs`, and returns the cancel. Calling it twice is safe. */
  readonly after: (delayMs: number, run: () => void) => () => void;
  /** The current instant, in epoch milliseconds. */
  readonly now: () => number;
}

/**
 * MAX_TIMER_MS is the longest delay a timer honours, in milliseconds: the largest signed 32-bit
 * integer. A runtime handed a longer delay fires the timer at once.
 *
 * Declared beside {@link Clock} and nowhere else in this package. An engine that takes a delay
 * from its caller refuses one past it by importing this constant, and never states the number.
 */
export const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * systemClock reads the platform's own clock and timers.
 *
 * The default everywhere, and the one every environment this family runs in already has:
 * `Date.now` and `setTimeout` are in Node, in a worker, in a browser and in React Native.
 */
export const systemClock = (): Clock => ({
  after: (delayMs, run) => {
    const handle = setTimeout(run, delayMs);

    return () => {
      clearTimeout(handle);
    };
  },
  now: () => Date.now(),
});

/**
 * manualClock is a clock a caller advances by hand: nothing fires until `advance` reaches it.
 *
 * Published because the alternative is every consumer writing their own, and because the two
 * places it is genuinely needed are not tests. A studio rendered into a video frame server-side
 * has no wall clock worth waiting on; a replay tool stepping through a recorded session needs
 * time to move at the pace of the recording rather than of the machine playing it.
 *
 * Timers fire in DUE order rather than in the order they were armed, which is what a real timer
 * queue does and what a naive array does not: a four-second banner armed before a one-second
 * rename must not fire first.
 *
 * @param start - The instant the clock reads before its first `advance`, in epoch
 *   milliseconds. Defaults to `0`.
 * @returns A {@link Clock} with `advance(byMs)`, which moves it forward and runs every timer
 *   that falls due.
 */
export const manualClock = (start = 0): Clock & { advance: (byMs: number) => void } => {
  let current = start;
  let nextId = 0;
  const pending = new Map<number, { due: number; run: () => void }>();

  return {
    advance: (byMs) => {
      current += byMs;

      // Snapshot before running: a callback that arms another timer must not be able to make
      // this loop reach it in the same tick, which is how a self-rescheduling hint becomes an
      // infinite loop the moment a test advances by a minute.
      const due = [...pending.entries()]
        .filter(([, timer]) => timer.due <= current)
        .sort(([, left], [, right]) => left.due - right.due);

      for (const [id, timer] of due) {
        // Asked again at its turn: an earlier timer of this advance may have cancelled it, and a
        // cancelled timer never runs.
        if (!pending.delete(id)) {
          continue;
        }

        timer.run();
      }
    },
    after: (delayMs, run) => {
      const id = nextId;
      nextId += 1;
      pending.set(id, { due: current + delayMs, run });

      return () => {
        pending.delete(id);
      };
    },
    now: () => current,
  };
};

/**
 * repeatEvery runs `run` every `delayMs` until the returned cancel is called.
 *
 * A free function over {@link Clock} rather than a third method on it, and that is the whole
 * point: a Dart or Swift port implements `now` and `after` and gets this for free, where a third
 * member would be a third thing to get right. It is also the shape that is actually correct:
 * each run schedules the NEXT one, so a tick whose body takes longer than the interval cannot
 * stack up behind itself the way a raw `setInterval` does.
 */
export const repeatEvery = (clock: Clock, delayMs: number, run: () => void): (() => void) => {
  let cancel: () => void = () => undefined;
  let stopped = false;

  const arm = (): void => {
    cancel = clock.after(delayMs, () => {
      if (stopped) {
        return;
      }

      run();
      arm();
    });
  };

  arm();

  return () => {
    stopped = true;
    cancel();
  };
};
